import { get } from "http";
import {
  Game,
  Producer,
  checkIfMoleculeUnlocked,
  speedFactor,
  times100Multiplier,
  times10Multiplier,
} from "../types/game";
import Decimal from "break_infinity.js";

export function getCost(producer: Producer): Decimal {
  return innerGetCost(
    producer.baseCost,
    producer.costScaling,
    producer.upgradeLevel
  );
}

/* export function innerGetCost(upgradeLevel: number, layerLevel: number): number {
  const costIncreaseLevel = Math.floor(upgradeLevel / 10);
  const scaleFactor = layerLevel - 1;

  let baseCost = 10 ** layerLevel * 10 ** scaleFactor;
  if (layerLevel === 2) baseCost = 1000;
  if (costIncreaseLevel === 0) return baseCost;
  return Math.floor(Math.pow(baseCost, 1.1 + costIncreaseLevel / 10));
} */
export function innerGetCost(
  baseCost: number,
  scaleFactor: number,
  upgradeLevel: number
): Decimal {
  const baseCostDecimal = new Decimal(baseCost);
  const scaleFactorDecimal = new Decimal(scaleFactor);

  return baseCostDecimal.times(
    scaleFactorDecimal.pow(Math.floor(upgradeLevel / 10))
  );
}

export function getProgress(
  goal: number | Decimal,
  current: number | Decimal
): number {
  if (typeof goal === "number" && typeof current === "number") {
    if (goal === 0) return 100;
    if (current === 0) return 0;
    let progress = Math.floor(current / goal) * 10;
    return Math.min(progress, 100);
  }

  goal = new Decimal(goal);
  current = new Decimal(current);
  if (goal.eq(0)) return 100;
  if (current.eq(0)) return 0;
  let progress = Math.floor(current.div(goal).toNumber()) * 10;
  return Math.min(progress, 100);
}

export function getTimes10Progress(upgradeLevel: number) {
  return upgradeLevel % 10;
}
export function getTimes10Multiplier(
  upgradeLevel: number,
  multiplier: number,
  game: Game
) {
  const times10Level = Math.floor(upgradeLevel / 10);
  if (times10Level === 0) return 1;
  if (Object.keys(game.molecule.graphNodes).includes("Water")) {
    return (
      times10Level *
      (multiplier + times10Level * game.molecule.graphNodes["Water"])
    );
  }
  return times10Level * multiplier;
}
export function getTimes100Multiplier(
  upgradeLevel: number,
  multiplier: number,
  game: Game
) {
  const times100Level = Math.floor(upgradeLevel / 100);
  if (times100Level === 0) return 1;
  if (Object.keys(game.molecule.graphNodes).includes("Carbon Dioxide")) {
    return (
      times100Level *
      (multiplier + times100Level * game.molecule.graphNodes["Carbon Dioxide"])
    );
  }
  return times100Level * multiplier;
}
export function generateProducts(
  producer: Producer,
  achievementMultiplier: number,
  game: Game
): Decimal {
  const times100MultiplierUnlocked = game.unlockedAchievementIds.has(12);
  const final10Multiplier = getTimes10Multiplier(
    producer.upgradeLevel,
    times10Multiplier,
    game
  );
  let final100Multiplier = 1;
  if (times100MultiplierUnlocked) {
    final100Multiplier = getTimes100Multiplier(
      producer.upgradeLevel,
      times100Multiplier,
      game
    );
  }
  let oxygenMultiplier = 1;
  if (Object.keys(game.molecule.graphNodes).includes("Oxygen")) {
    oxygenMultiplier = game.molecule.graphNodes["Oxygen"] * 2.5;
  }
  const res = producer.value
    .times(
      producer.multiplier.times(
        final10Multiplier *
          achievementMultiplier *
          final100Multiplier *
          oxygenMultiplier *
          game.neutronState.multiplierMultiplier
      )
    )
    .floor();
  return res;
}

export function getParticleStartValue(game: Game) {
  if (!game || !Object.keys(game.molecule.graphNodes).includes("Hydrogen"))
    return 10;
  let particleStartValue = 10;
  for (let i = 0; i < game.molecule.graphNodes["Hydrogen"]; i++) {
    particleStartValue *= 10000;
  }
  return particleStartValue + 10;
}

export function getNextXUpgradeCosts(producer: Producer, x: number): Decimal {
  let costs = new Decimal(0);
  for (let i = 0; i < x; i++) {
    costs = costs.plus(
      innerGetCost(
        producer.baseCost,
        producer.costScaling,
        producer.upgradeLevel + i
      )
    );
  }
  return costs;
}

export function getCostsTillNextTimes10Upgrade(producer: Producer): Decimal {
  const times10Progress = getTimes10Progress(producer.upgradeLevel);
  const tillNext = 10 - times10Progress;
  const costs = getNextXUpgradeCosts(producer, tillNext);
  return costs;
}

export function buyAllUntil10(
  producers: Producer[],
  setGame: React.Dispatch<React.SetStateAction<Game>>,
  isAutoBuy?: boolean
) {
  let particles = producers[0].value;
  producers.forEach((producer, index) => {
    if (index === 0) return;
    if (!producer.unlocked) return;
    if (isAutoBuy && !producer.automateBuy) return;
    let costs = getCostsTillNextTimes10Upgrade(producer);
    if (costs.lessThan(particles)) {
      setGame((prev) => ({
        ...prev,
        producers: prev.producers.map((prevProducer, prevIndex) => {
          if (prevIndex === 0)
            return {
              ...prevProducer,
              value: prevProducer.value.minus(costs),
            };
          if (prevIndex === index) {
            return {
              ...prevProducer,
              upgradeLevel:
                prevProducer.upgradeLevel +
                10 -
                getTimes10Progress(prevProducer.upgradeLevel),
              value: prevProducer.value.plus(
                10 - getTimes10Progress(prevProducer.upgradeLevel)
              ),
            };
          } else {
            return prevProducer;
          }
        }),
      }));
      particles = new Decimal(particles.minus(costs));
    }
  });
}

export function calculateMoleculeProgress(
  particles: Decimal,
  goal: number
): number {
  if (particles.equals(0)) return 0;
  const progress = (particles.log10() / Math.log10(goal)) * 100;
  return Math.min(progress, 100);
}

export function calculateProductPerSecond(
  producer: Producer,
  achievementMultiplier: number,
  game: Game
) {
  const generatedProducts = generateProducts(
    producer,
    achievementMultiplier,
    game
  );

  return generatedProducts.divideBy(
    (producer.produceTime * game.neutronState.timeMultiplier) / 1000
  );
}

export function addOfflineProgressAndGetDiff(game: Game | undefined) {
  const diff: { name: string; diff: Decimal }[] = [];
  if (game && game.timestamp) {
    const { producers } = game;
    const now = Date.now();
    const timePassed = now - game.timestamp;
    const secondsPassed = timePassed / 1000;
    producers.forEach((producer, index) => {
      if (index === 0) return;
      const product = producers[index - 1];
      diff.push({ name: product.name, diff: new Decimal(0) });
      if (!producer.automateCollect) return;
      const productsPerSecond = calculateProductPerSecond(producer, 1, game); //TODO: add achievement multiplier, but offline progress can be slower
      const productsGenerated = productsPerSecond.times(secondsPassed);
      const offlineProductsGenerated = productsGenerated.times(0.6); // offline factor
      diff.push({ name: product.name, diff: offlineProductsGenerated });
    });
  }
  return { producers: diff };
}

export function getPsToMultiplyId(
  psUpgradeLevel: number,
  producersLength: number
) {
  return (psUpgradeLevel + 1) % (producersLength - 1) || producersLength - 1;
}

export function getUnlockedMoleculeNodes(game: Game) {
  const unlockedMoleculeNodes = new Set<string>();
  Object.keys(game.molecule.graphNodes).forEach((key) => {
    if (game.molecule.graphNodes[key] > 0) unlockedMoleculeNodes.add(key);
  });
  return unlockedMoleculeNodes;
}

export function calculateExpectedMoleculeValue(game: Game): Decimal {
  let m = calculateBaseMoleculeValue(game);
  if (checkIfMoleculeUnlocked("Ozone", game)) {
    m = m.times(Math.pow(2, game.molecule.graphNodes["Ozone"]));
  }
  return m;
}

export function calculateBaseMoleculeValue(game: Game): Decimal {
  let res = new Decimal(Math.floor(game.producers[0].value.log10() / 10) - 4);
  if (checkIfMoleculeUnlocked("Methane", game)) {
    res = res.min(game.molecule.graphNodes["Methane"] + 1);
    return res.lt(0) ? new Decimal(0) : res;
  }
  return res.lt(0) ? new Decimal(0) : new Decimal(1);
}

export function isMoleculeUnlocked(moleculeName: string, game: Game) {
  return Object.keys(game.molecule.graphNodes).includes(moleculeName);
}

export function getCarbonMonoxideMultiplier(upgradeLevel: number) {
  return Math.pow(10, upgradeLevel * 5);
}

export function buyAllSpeedShopUpgrades(
  game: Game,
  setGame: React.Dispatch<React.SetStateAction<Game>>
) {
  let particleValue = game.producers[0].value;
  game.producers.forEach((producer, index) => {
    if (index === 0) return;
    const cost = calculateSpeedShopCost(game, index);
    if (particleValue.gt(cost)) {
      setGame((prev) => {
        return {
          ...prev,
          producers: prev.producers.map((p, innerIndex) => {
            if (innerIndex === 0) return { ...p, value: p.value.minus(cost) };
            if (innerIndex === index) {
              return {
                ...p,
                speedUpgradeLevel: p.speedUpgradeLevel + 1,
                produceTime: calculateNewProduceTime(
                  p.produceTime,
                  speedFactor
                ),
              };
            }
            return p;
          }),
        };
      });
      particleValue = new Decimal(particleValue.minus(cost));
    }
  });
}

export function calculateNewProduceTime(
  produceTime: number,
  speedFactor: number
) {
  let newTime = produceTime;
  if (produceTime - produceTime * speedFactor < 10) {
    newTime = produceTime - 10;
  } else {
    newTime = produceTime * speedFactor;
  }
  if (newTime < 1) newTime = 1;
  return newTime;
}

export function calculateSpeedShopCost(
  game: Game,
  producerIndex: number
): Decimal {
  const producer = game.producers[producerIndex];
  const cost = innerGetCost(producer.baseCost, producer.costScaling, 1).times(
    new Decimal(10).pow(producer.speedUpgradeLevel)
  );
  if (cost.equals(10)) return new Decimal(11);
  if (isMoleculeUnlocked("Carbon Monoxide", game)) {
    const speedShopDivider = getCarbonMonoxideMultiplier(
      game.molecule.graphNodes["Carbon Monoxide"]
    );
    if (cost.lt(speedShopDivider)) {
      return new Decimal(0);
    }
    return cost.div(speedShopDivider);
  }
  return cost;
}

export function calculateNewProducerStrengthiningMultiplier(game: Game) {
  if (isMoleculeUnlocked("Hydrochloric Acid", game)) {
    const level = game.molecule.graphNodes["Hydrochloric Acid"];
    return game.producerStrengthening.multiplier.times(2 * Math.pow(2, level));
  }
  return game.producerStrengthening.multiplier.times(2);
}
